import { memo, useEffect, useRef, useState } from "react";

// Assets
import { Plus, Trash03 } from "@untitled-ui/icons-react";

// Components
import { ButtonV2 } from "#src/commonV2/Button";

type Props = {
  id: string;
  name: string;
  onEdit: (id: string, newName: string) => void;
  onAddChild: (parentId: string) => Promise<void>;
  onDelete: (id: string) => void;
};

export const DepartmentNode = memo((props: Props) => {
  const { id, name, onEdit, onAddChild, onDelete } = props;

  // --------------- DATA ---------------

  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentName, setCurrentName] = useState(name);
  const [inputValue, setInputValue] = useState(name);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // --------------- METHODS ---------------

  const autoFocus = () => {
    if (document.activeElement !== containerRef.current) {
      containerRef.current?.focus();
    }
  };

  const handleClick = () => {
    if (id !== "0") {
      // Prevent editing root node
      autoFocus();
      setInputValue(currentName);
      setIsEditing(true);
    }
  };

  // Handle saving the new name
  const handleSave = () => {
    setIsEditing(false);
    if (inputValue.trim() === "") {
      setInputValue(currentName);
      return;
    }
    if (inputValue.trim() !== currentName) {
      setCurrentName(inputValue.trim());
      onEdit(id, inputValue.trim());
    }
  };

  // Handle adding a child department
  const handleAddChild = async () => {
    setIsAdding(true);
    await onAddChild(id);
    setIsAdding(false);
  };

  // --------------- EFFECTS ---------------

  // Focus the input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  // --------------- RENDER ---------------

  return (
    <div
      id={`department-node-${id}`}
      ref={containerRef}
      className="tree-node__wrapper pr-4"
      tabIndex={0}
    >
      {/* Display name */}
      {!isEditing && (
        <div
          className="tree-node__title w-full flex items-center justify-center gap-2 h-full"
          onClick={handleClick}
        >
          <p>{currentName}</p>
        </div>
      )}
      {/* Display name end */}

      {/* Input field */}
      {isEditing && (
        <input
          ref={inputRef}
          type="text"
          className="tree-node__input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={() => {
            handleSave();
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              handleSave();
            }
            if (e.key === "Escape") {
              setIsEditing(false);
            }
          }}
        />
      )}
      {/* Input field end */}

      {/* Action buttons */}
      <div className={`tree-node__actions`}>
        <ButtonV2
          iconOnly
          size="sm"
          variant="secondary"
          color="brand"
          pending={isAdding}
          onClick={() => {
            autoFocus();
            handleAddChild();
          }}
        >
          <Plus className="h-5 w-5 text-brand-v2-700" />
        </ButtonV2>

        {id !== "0" && (
          <ButtonV2
            iconOnly
            size="sm"
            variant="secondary"
            color="error"
            disabled={isAdding}
            onClick={() => {
              autoFocus();
              onDelete(id);
            }}
          >
            <Trash03 className="h-5 w-5 text-error-v2-700" />
          </ButtonV2>
        )}
      </div>
      {/* Action buttons end */}
    </div>
  );
});

DepartmentNode.displayName = "DepartmentNode";
